import { Box } from "@outschool/backpack";
import { SavedSearchFilters } from "@outschool/gql-backend-generated";
import {
  SearchFilters,
  SearchUseCase
} from "@outschool/gql-frontend-generated";
import { useLocalStorageState } from "@outschool/local-storage";
import { onboardingQuizPath } from "@outschool/routes";
import {
  AuthTrigger,
  useLoginFlowContext,
  useSession
} from "@outschool/ui-auth";
import React from "react";

import HeroImageJpg from "../../../../images/hero_image_bclp.jpg";
import * as Env from "../../../../shared/Env";
import useTrackSearchFiltersApplied from "../../../hooks/useTrackSearchFiltersApplied";
import CategoryListings from "../basic/CategoryListings";
import CuratedCategoryPageActivityGrid from "../curated/CuratedCategoryActivityGrid";
import { CategoryListingsFilters } from "../filters/CategoryListingsFilters";
import { useListingsData } from "../hooks/useListingsData";
import { PaidClp } from "../hooks/usePaidClpQuery";
import { PaidCategoryHeroSection } from "./PaidCategoryHeroSection";

export default function PaidCategoryPageBody({
  categoryPage,
  categorySlug
}: {
  categoryPage: PaidClp;
  categorySlug: string;
}) {
  const { searchUid, trackSearchFiltersApplied } =
    useTrackSearchFiltersApplied();
  const [signupModalTriggered, setSignupModalTriggered] = useLocalStorageState(
    "paidClpSignupModalTriggered",
    false
  );
  const { isLoggedIn } = useSession();
  const { enterLoginFlow } = useLoginFlowContext();
  const shouldTriggerSignupModal = !signupModalTriggered && !isLoggedIn;
  const tryTriggerSignupModal = () => {
    if (shouldTriggerSignupModal) {
      setSignupModalTriggered(true);
      enterLoginFlow({
        authTrigger: AuthTrigger.PAID_CLP,
        newUrl: onboardingQuizPath(),
        shouldNavigateOverwrite: false
      });
      return true;
    }
    return false;
  };

  const listingsSlug =
    categoryPage?.contentfulOverrides?.featuredClassesOverrideSlug ||
    categorySlug;
  const {
    error: listingError,
    exactTotal,
    fetchMore,
    listings,
    loading: loadingListings,
    isFetchingMore,
    updateSearchFilters,
    searchFilters
  } = useListingsData({
    categorySlug: listingsSlug,
    predefinedCategoryFilters: categoryPage?.searchFilters,
    trackSearchFiltersApplied,
    pageLocation: "PAID_CATEGORY",
    useCase: SearchUseCase.CategoryContent
  });

  const title =
    categoryPage.contentfulOverrides?.heroTitle || categoryPage.title;
  const heroImageUrl =
    categoryPage?.contentfulOverrides?.heroImageUrl || HeroImageJpg;
  const listingsSection = categoryPage?.contentfulOverrides ? (
    <CuratedCategoryPageActivityGrid
      loadedCategory={categoryPage! as any}
      error={listingError}
      exactTotal={exactTotal}
      fetchMore={fetchMore}
      listings={listings}
      loading={loadingListings}
      searchUid={searchUid}
      isTutoringCLP={false}
      searchFilters={categoryPage?.searchFilters || ({} as SearchFilters)}
    />
  ) : (
    <CategoryListings
      fetchMore={fetchMore}
      categorySlug={listingsSlug!}
      exactTotal={exactTotal}
      isFetchingMore={isFetchingMore}
      listings={listings}
      loading={loadingListings}
      isReadOnlyMode={Env.IS_READ_ONLY_MODE}
      searchUid={searchUid}
      pageLocation={"PAID_CATEGORY"}
    />
  );
  const listingsSectionWithSignupTrigger = shouldTriggerSignupModal ? (
    <Box sx={{ position: "relative" }}>
      <Box
        sx={{
          zIndex: 10,
          position: "absolute",
          width: "100%",
          height: "100%"
        }}
        onClick={tryTriggerSignupModal}
        onKeyDown={tryTriggerSignupModal}
      ></Box>
      {listingsSection}
    </Box>
  ) : (
    listingsSection
  );
  return (
    <Box>
      <PaidCategoryHeroSection
        title={title}
        heroImageUrl={heroImageUrl}
        tryTriggerSignupModal={tryTriggerSignupModal}
      />
      <CategoryListingsFilters
        updateListings={updateSearchFilters}
        searchFilters={searchFilters}
        preSetFilters={searchFilters as SavedSearchFilters}
      />
      {listingsSectionWithSignupTrigger}
    </Box>
  );
}
